import { AppListenerMiddleware } from "@src/store";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { sendWebsocketMessage } from "@src/store/slices/global";
import { v4 } from "uuid";
import { getShortOrgId } from "@src/store/slices/organization";
import { getPluginConfig } from "@src/api/plugin-configs/get-plugin-config";
import { AppListenerApi } from "@src/store/rootState";

const DEPLOY_TIMEOUT_MS = 60 * 1000;

const { setIsDeploying, setCacheKey, setPluginConfigState, setLoadState } =
  experienceBuilderSlice.actions;

export const setupDeployListener = (middleware: AppListenerMiddleware) =>
  middleware.startListening({
    predicate: (action) => setIsDeploying.match(action) && action.payload,
    effect: async (_, api) => {
      const result = await api.take((action) => {
        if (
          sendWebsocketMessage.match(action) &&
          action.payload.type === "configDeployed"
        ) {
          return true;
        }
        if (setIsDeploying.match(action)) {
          return true;
        }
        return false;
      }, DEPLOY_TIMEOUT_MS);

      const takeAction = result?.[0];
      // if setIsDeploying is true, we want to keep it that way
      // if setIsDeploying is false, there is no need to set it to false
      if (setIsDeploying.match(takeAction)) {
        return;
      }
      if (sendWebsocketMessage.match(takeAction)) {
        const [meta] = await Promise.all([
          updateConfig(api),
          reloadIFrame(api),
        ]);
        if (meta) {
          api.dispatch(
            setPluginConfigState({
              status: "loaded",
              version: meta?.embeddedPreviewVersion,
            })
          );
        } else {
          api.dispatch(
            setPluginConfigState({ status: "error", version: undefined })
          );
        }
      }
      api.dispatch(setIsDeploying(false));
    },
  });

async function updateConfig(api: AppListenerApi) {
  const shortOrgId = getShortOrgId(api.getState());
  try {
    return await getPluginConfig(shortOrgId);
  } catch {
    return undefined;
  }
}

async function reloadIFrame(api: AppListenerApi) {
  api.dispatch(setCacheKey(v4()));
  await api.take((action) =>
    Boolean(setLoadState.match(action) && action.payload)
  );
}
